import { attempt } from '@shared/utils'
import { logger } from '@shared/lib/logger'
import type { UnifiedProject, UnifiedProjectsResponse } from '../types/work-entity.types'

interface FetchWorkEntitiesParams {
  numberOfDocuments?: number
}

/**
 * Fetches the shopper's newest projects as a single merged, sorted feed. The
 * backend fetches WES work entities and Albelli documents, merges and sorts
 * them, and returns the newest `numberOfDocuments`. Returns an empty list on
 * any error so the page can still render.
 */
export const fetchWorkEntities = async (
  api: string,
  authorizationHeader: string,
  tenant: string,
  { numberOfDocuments = 10 }: FetchWorkEntitiesParams = {},
): Promise<UnifiedProject[]> => {
  const url = new URL(`${api}/api/v1/external/work-entities`)
  url.searchParams.set('numberOfDocuments', String(numberOfDocuments))
  url.searchParams.set('sortBy', 'LastModified')
  url.searchParams.set('includeHidden', 'false')
  if (tenant) {
    url.searchParams.set('tenant', tenant)
  }

  const { data: response, error: fetchError } = await attempt(
    fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: authorizationHeader,
      },
    }),
  )

  if (fetchError) {
    logger.error('Error fetching work entities', {
      action: 'fetch_work_entities_error',
      errorMessage: JSON.stringify(fetchError),
    })
    return []
  }

  if (!response.ok) {
    logger.error('Error fetching work entities', {
      action: 'fetch_work_entities_error',
      status: response.status,
      statusText: response.statusText,
    })
    return []
  }

  const { data: json, error: parseError } = await attempt(response.json() as Promise<UnifiedProjectsResponse>)

  if (parseError) {
    logger.error('Error fetching work entities', {
      action: 'fetch_work_entities_parse_error',
      errorMessage: String(parseError),
    })
    return []
  }

  return json?.data?.projects ?? []
}
